import { RawCommands, getNodeType } from '@tiptap/core';
import ListItem from '@tiptap/extension-list-item';
import { Fragment, NodeType, Slice } from 'prosemirror-model';
import { Command } from 'prosemirror-state';
import { ReplaceAroundStep } from 'prosemirror-transform';
import BulletList from './BulletList';

function originalSinkListItem(itemType: NodeType, token: string): Command {
  return function (state, dispatch) {
    const { $from, $to } = state.selection;
    const range = $from.blockRange(
      $to,
      node => node.childCount > 0 && node.firstChild!.type === itemType,
    );
    if (!range) return false;
    const startIndex = range.startIndex;
    if (startIndex === 0) return false;
    const parent = range.parent,
      nodeBefore = parent.child(startIndex - 1);
    if (nodeBefore.type !== itemType) return false;

    if (dispatch) {
      const attrs = parent.type.name === BulletList.name ? { token } : null;
      const nestedBefore = nodeBefore.lastChild && nodeBefore.lastChild.type === parent.type;
      const inner = Fragment.from(nestedBefore ? itemType.create() : null);
      const slice = new Slice(
        Fragment.from(itemType.create(null, Fragment.from(parent.type.create(attrs, inner)))),
        nestedBefore ? 3 : 1,
        0,
      );
      const before = range.start,
        after = range.end;
      dispatch(
        state.tr
          .step(
            new ReplaceAroundStep(
              before - (nestedBefore ? 3 : 1),
              after,
              before,
              after,
              slice,
              1,
              true,
            ),
          )
          .scrollIntoView(),
      );
    }
    return true;
  };
}

const sinkListItem: RawCommands['sinkListItem'] =
  typeOrName =>
  ({ state, dispatch }) => {
    const type = getNodeType(typeOrName, state.schema);
    const { $from } = state.selection;
    const parentList = $from.node(-2); // Get the parent list node
    const token = parentList.attrs.token || '*';

    return originalSinkListItem(type, token)(state, dispatch);
  };

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    listItem: {
      /**
       * Sink a list item
       */
      customSinkListItem: (typeOrName: string | NodeType) => ReturnType;
    };
  }
}

export default ListItem.extend({
  addCommands() {
    return {
      customSinkListItem: (typeOrName: string | NodeType) => sinkListItem(typeOrName),
    };
  },
  addKeyboardShortcuts() {
    return Object.assign({}, this.parent?.() || {}, {
      Tab: () => this.editor.commands.customSinkListItem(this.name),
    });
  },
});
